/**
 * Phase 7-B — Marketplace v2 browse page (rewritten from the old list view).
 *
 * Surfaces `GET /marketplace/listings` (`02_api_spec.md §3.2`) with:
 *  - free-text search (debounced 300ms)
 *  - capability filter (text + suggestion chips)
 *  - price range, autonomy multiselect, trust min, has-badges toggle
 *  - sort selector
 *  - cursor pagination via "Load more"
 *  - active filter chips you can click to remove
 *  - empty state with "clear filters" CTA
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import Badge from '../components/Badge';
import {
  weiToAdi,
  adiToWei,
  listingStatusBadge,
  useDebounced,
} from './marketplace/utils';
import type {
  ListingStatus,
  ListingsPage,
  MarketplaceListing,
  PricingModel,
} from '@infinia/ida-sdk';

const SUGGESTED_CAPABILITIES = [
  'shop:negotiate',
  'lending:eligibility-check',
  'analytics:report',
  'devops:deploy',
  'legal:review',
];

const AUTONOMY_LEVELS = ['intern', 'junior', 'senior', 'principal'] as const;
type AutonomyLevel = typeof AUTONOMY_LEVELS[number];

type SortKey =
  | 'created_at_desc'
  | 'price_asc'
  | 'price_desc'
  | 'trust_desc'
  | 'badges_desc';

interface FilterState {
  q: string;
  capability: string;
  priceMinAdi: string;
  priceMaxAdi: string;
  pricingModel: '' | PricingModel;
  autonomy: AutonomyLevel[];
  trustMin: number;
  hasBadges: boolean;
  status: ListingStatus;
  sort: SortKey;
}

const INITIAL_FILTERS: FilterState = {
  q: '',
  capability: '',
  priceMinAdi: '',
  priceMaxAdi: '',
  pricingModel: '',
  autonomy: [],
  trustMin: 0,
  hasBadges: false,
  status: 'active',
  sort: 'created_at_desc',
};

function pricingLabelKey(model: PricingModel | undefined): string {
  switch (model) {
    case 'per_call': return 'browse.card.perCall';
    case 'per_hour': return 'browse.card.perHour';
    case 'flat_fee': return 'browse.card.flatFee';
    default: return 'browse.card.perCall';
  }
}

export default function AgentMarketplace() {
  const navigate = useNavigate();
  const { t } = useTranslation(['marketplace', 'common']);

  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const debounced = useDebounced(filters, 300);

  const [items, setItems] = useState<MarketplaceListing[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildQuery = useCallback((f: FilterState, c?: string | null) => {
    const sp = new URLSearchParams();
    if (f.q.trim()) sp.set('q', f.q.trim());
    if (f.capability.trim()) sp.set('capability', f.capability.trim());
    if (f.priceMinAdi.trim()) sp.set('priceMinWei', adiToWei(f.priceMinAdi));
    if (f.priceMaxAdi.trim()) sp.set('priceMaxWei', adiToWei(f.priceMaxAdi));
    if (f.pricingModel) sp.set('pricingModel', f.pricingModel);
    if (f.autonomy.length === 1) sp.set('autonomy', f.autonomy[0]);
    if (f.trustMin > 0) sp.set('trustMin', String(f.trustMin));
    if (f.hasBadges) sp.set('hasBadges', 'true');
    if (f.status) sp.set('status', f.status);
    if (f.sort) sp.set('sort', f.sort);
    sp.set('limit', '20');
    if (c) sp.set('cursor', c);
    return `/marketplace/listings?${sp.toString()}`;
  }, []);

  const fetchPage = useCallback(async (f: FilterState, c?: string | null) => {
    const isInitial = !c;
    if (isInitial) setLoading(true); else setLoadingMore(true);
    setError(null);
    try {
      const res = await apiFetch<ListingsPage>(buildQuery(f, c));
      setItems((prev) => (isInitial ? res.items : [...prev, ...res.items]));
      setCursor(res.nextCursor ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    void fetchPage(debounced, null);
  }, [debounced, fetchPage]);

  const updateFilter = <K extends keyof FilterState>(k: K, v: FilterState[K]) => {
    setFilters((prev) => ({ ...prev, [k]: v }));
  };

  const toggleAutonomy = (lvl: AutonomyLevel) => {
    setFilters((prev) => ({
      ...prev,
      autonomy: prev.autonomy.includes(lvl)
        ? prev.autonomy.filter((x) => x !== lvl)
        : [...prev.autonomy, lvl],
    }));
  };

  const clearFilters = () => setFilters(INITIAL_FILTERS);

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (filters.q) chips.push({ key: 'q', label: `"${filters.q}"`, clear: () => updateFilter('q', '') });
    if (filters.capability) chips.push({ key: 'cap', label: filters.capability, clear: () => updateFilter('capability', '') });
    if (filters.priceMinAdi) chips.push({ key: 'pmin', label: `≥ ${filters.priceMinAdi} ADI`, clear: () => updateFilter('priceMinAdi', '') });
    if (filters.priceMaxAdi) chips.push({ key: 'pmax', label: `≤ ${filters.priceMaxAdi} ADI`, clear: () => updateFilter('priceMaxAdi', '') });
    if (filters.pricingModel) chips.push({ key: 'pm', label: t(`browse.pricingModels.${filters.pricingModel}`), clear: () => updateFilter('pricingModel', '') });
    for (const a of filters.autonomy) chips.push({ key: `a-${a}`, label: t(`browse.autonomyLevels.${a}`), clear: () => toggleAutonomy(a) });
    if (filters.trustMin > 0) chips.push({ key: 'trust', label: `Trust ≥ ${filters.trustMin}`, clear: () => updateFilter('trustMin', 0) });
    if (filters.hasBadges) chips.push({ key: 'badges', label: t('browse.filters.hasBadges'), clear: () => updateFilter('hasBadges', false) });
    return chips;
  }, [filters, t]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('subtitle')}</p>
      </div>

      {/* Search + sort row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-text-faint" style={{ fontSize: 18 }}>search</span>
          <input
            type="text"
            value={filters.q}
            onChange={(e) => updateFilter('q', e.target.value)}
            placeholder={t('browse.searchPlaceholder')}
            className="h-10 w-full rounded-lg border border-border-subtle bg-white pl-10 pr-4 text-sm placeholder:text-text-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <select
          value={filters.sort}
          onChange={(e) => updateFilter('sort', e.target.value as SortKey)}
          className="h-10 rounded-lg border border-border-subtle bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          {(['created_at_desc', 'price_asc', 'price_desc', 'trust_desc', 'badges_desc'] as SortKey[]).map((s) => (
            <option key={s} value={s}>{t(`browse.sortOptions.${s}`)}</option>
          ))}
        </select>
      </div>

      {/* Capability suggestion chips */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={filters.capability}
          onChange={(e) => updateFilter('capability', e.target.value)}
          placeholder={t('browse.filters.capabilityPlaceholder')}
          className="h-9 w-64 rounded-lg border border-border-subtle bg-white px-3 text-sm placeholder:text-text-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        <span className="text-[10px] uppercase tracking-widest font-bold text-text-faint">{t('browse.filters.capability')}:</span>
        {SUGGESTED_CAPABILITIES.map((cap) => (
          <button
            key={cap}
            onClick={() => updateFilter('capability', cap)}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${filters.capability === cap ? 'border-primary bg-primary/10 text-primary' : 'border-border-subtle text-text-muted hover:bg-slate-50'}`}
          >
            {cap}
          </button>
        ))}
      </div>

      {/* Filter row 2 — price, autonomy, trust, badges */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 rounded-xl border border-border-subtle bg-white p-4">
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-text-muted">{t('browse.filters.priceRange')}</label>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="0"
              step="0.01"
              value={filters.priceMinAdi}
              onChange={(e) => updateFilter('priceMinAdi', e.target.value)}
              placeholder={t('browse.filters.priceMin')}
              className="h-9 w-full rounded-lg border border-border-subtle bg-white px-2 text-sm"
            />
            <span className="text-text-faint">–</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={filters.priceMaxAdi}
              onChange={(e) => updateFilter('priceMaxAdi', e.target.value)}
              placeholder={t('browse.filters.priceMax')}
              className="h-9 w-full rounded-lg border border-border-subtle bg-white px-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-text-muted">{t('browse.filters.autonomy')}</label>
          <div className="flex flex-wrap gap-1">
            {AUTONOMY_LEVELS.map((lvl) => (
              <button
                key={lvl}
                onClick={() => toggleAutonomy(lvl)}
                className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase ${filters.autonomy.includes(lvl) ? 'border-primary bg-primary/10 text-primary' : 'border-border-subtle text-text-muted hover:bg-slate-50'}`}
              >
                {t(`browse.autonomyLevels.${lvl}`)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-text-muted">
            {t('browse.filters.trustMin', { value: filters.trustMin })}
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={filters.trustMin}
            onChange={(e) => updateFilter('trustMin', Number(e.target.value))}
            className="w-full"
          />
        </div>

        <div className="flex flex-col justify-between gap-2">
          <label className="flex items-center gap-2 text-xs font-bold text-text-muted">
            <input
              type="checkbox"
              checked={filters.hasBadges}
              onChange={(e) => updateFilter('hasBadges', e.target.checked)}
              className="rounded"
            />
            {t('browse.filters.hasBadges')}
          </label>
          <select
            value={filters.pricingModel}
            onChange={(e) => updateFilter('pricingModel', e.target.value as '' | PricingModel)}
            className="h-9 rounded-lg border border-border-subtle bg-white px-2 text-sm"
          >
            <option value="">{t('browse.filters.pricingModelAny')}</option>
            <option value="per_call">{t('browse.pricingModels.per_call')}</option>
            <option value="per_hour">{t('browse.pricingModels.per_hour')}</option>
            <option value="flat_fee">{t('browse.pricingModels.flat_fee')}</option>
          </select>
        </div>
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-bold text-text-faint">{t('browse.filters.active')}:</span>
          {activeChips.map((c) => (
            <button
              key={c.key}
              onClick={c.clear}
              className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-slate-50 px-2 py-1 text-[11px] font-semibold text-text-muted hover:bg-slate-100"
            >
              {c.label}
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
            </button>
          ))}
          <button
            onClick={clearFilters}
            className="rounded-full border border-border-subtle px-2 py-1 text-[11px] font-bold text-primary hover:bg-primary/5"
          >
            {t('browse.filters.clear')}
          </button>
        </div>
      )}

      {/* Listings grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="text-center py-20 text-red-500 text-sm">{error}</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border-subtle bg-white p-12 text-center">
          <p className="text-base font-bold text-text-main">{t('browse.empty.title')}</p>
          <p className="mt-1 text-sm text-text-muted">{t('browse.empty.subtitle')}</p>
          <button onClick={clearFilters} className="btn-primary mt-4 inline-flex">
            {t('browse.empty.clear')}
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {items.map((listing) => {
              const trust = listing.trustSnapshot?.trustScore ?? 0;
              const badgeCount = listing.trustSnapshot?.badgeCount ?? 0;
              const trustColor = trust >= 80 ? 'bg-emerald-500' : trust >= 60 ? 'bg-amber-500' : 'bg-red-500';
              return (
                <button
                  key={listing.id}
                  onClick={() => navigate(`/marketplace/listings/${listing.id}`)}
                  className="text-left rounded-xl border border-border-subtle bg-white p-5 transition-shadow hover:shadow-md"
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-bold text-text-main">{listing.title}</p>
                      <p className="truncate text-[10px] text-text-faint">{listing.capabilityScope}</p>
                    </div>
                    <Badge variant={listingStatusBadge(listing.status)}>
                      {t(`browse.statusLabels.${listing.status}`)}
                    </Badge>
                  </div>

                  {listing.description && (
                    <p className="mb-3 line-clamp-2 text-xs text-text-muted">{listing.description}</p>
                  )}

                  <div className="mb-3 flex items-baseline gap-1">
                    <span className="text-base font-black text-text-main">
                      {weiToAdi(listing.pricing.priceWei)}
                    </span>
                    <span className="text-[11px] font-bold text-text-muted">{listing.pricing.currency}</span>
                    <span className="text-[10px] text-text-faint">{t(pricingLabelKey(listing.pricing.model))}</span>
                  </div>

                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-text-muted">{t('browse.card.trust')}</span>
                      <span className="text-sm font-black text-text-main">{trust}</span>
                      <div className="h-1.5 w-16 rounded-full bg-slate-100">
                        <div className={`h-full rounded-full ${trustColor}`} style={{ width: `${Math.max(0, Math.min(100, trust))}%` }} />
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-700">
                      <span className="material-symbols-outlined" style={{ fontSize: 12 }}>workspace_premium</span>
                      {t('browse.card.badges', { count: badgeCount })}
                    </span>
                  </div>

                  <div className="text-xs font-bold text-primary">{t('browse.card.viewListing')} →</div>
                </button>
              );
            })}
          </div>

          {cursor && (
            <div className="flex justify-center pt-4">
              <button
                onClick={() => void fetchPage(debounced, cursor)}
                disabled={loadingMore}
                className="btn-secondary"
              >
                {loadingMore ? t('browse.loadMoreLoading') : t('browse.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
